import { authenticatedFetch } from './authenticatedFetch';

const ARKHAM_ATTRIBUTION_ENDPOINT =
  '/api/arkham/arkham_attribution_batch.php';
const ARKHAM_NOTE_ENRICHMENT_ENDPOINT =
  '/api/arkham/arkham_note_enrichment_batch.php';
const ARKHAM_VALUE_THREAD_ENDPOINT =
  '/api/arkham/arkham_value_thread.php';
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

export class ArkhamApiError extends Error {
  constructor(message, status = 0, requestId = '', payload = null) {
    super(message);
    this.name = 'ArkhamApiError';
    this.status = Number(status || 0);
    this.requestId = String(requestId || '');
    this.payload = payload;
  }
}

function requestIdFrom(response, data) {
  return (
    response?.headers?.get?.('X-Request-ID') ||
    data?.request_id ||
    data?.requestId ||
    ''
  );
}

function normalizeErrorMessage(response, data, fallback) {
  const requestId = requestIdFrom(response, data);
  const serverMessage =
    typeof data?.message === 'string' && data.message.trim()
      ? data.message.trim()
      : typeof data?.error === 'string' && data.error.trim()
        ? data.error.trim()
        : fallback;

  return {
    message: requestId
      ? `${serverMessage} (Request ${requestId})`
      : serverMessage,
    requestId,
  };
}

async function postArkhamJson(endpoint, payload, options = {}) {
  const timeoutMs = Number(options.timeoutMs || DEFAULT_TIMEOUT_MS);
  const controller = new AbortController();
  const externalSignal = options.signal || null;
  let timedOut = false;
  let externalAbortListener = null;

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalAbortListener = () => controller.abort(externalSignal.reason);
      externalSignal.addEventListener('abort', externalAbortListener, { once: true });
    }
  }

  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort('timeout');
  }, Math.max(1000, timeoutMs));

  try {
    const response = await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload || {}),
      signal: controller.signal,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      const requestId = response.headers.get('X-Request-ID') || '';
      throw new ArkhamApiError(
        requestId
          ? `The Arkham endpoint returned an invalid response. (Request ${requestId})`
          : 'The Arkham endpoint returned an invalid response.',
        response.status,
        requestId
      );
    }

    if (!response.ok || !data || data.status === 'error') {
      const normalized = normalizeErrorMessage(
        response,
        data,
        'The Arkham request could not be completed.'
      );
      throw new ArkhamApiError(
        normalized.message,
        response.status,
        normalized.requestId,
        data
      );
    }

    return {
      data,
      requestId: requestIdFrom(response, data),
      response,
    };
  } catch (error) {
    if (error?.name === 'AbortError') {
      if (!timedOut && externalSignal?.aborted) {
        throw new ArkhamApiError(
          'The Arkham request was cancelled.',
          0,
          ''
        );
      }
      throw new ArkhamApiError(
        'The Arkham request timed out before it could finish.',
        0,
        ''
      );
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    if (externalSignal && externalAbortListener) {
      externalSignal.removeEventListener('abort', externalAbortListener);
    }
  }
}

export function postArkhamAttributionBatch(payload, options = {}) {
  return postArkhamJson(
    ARKHAM_ATTRIBUTION_ENDPOINT,
    {
      ...(payload || {}),
      client_context: 'tool77',
    },
    options
  );
}

export function postArkhamNoteEnrichmentBatch(
  payload,
  options = {}
) {
  return postArkhamJson(
    ARKHAM_NOTE_ENRICHMENT_ENDPOINT,
    {
      ...(payload || {}),
      client_context: options.clientContext || 'tool86',
      // The hardened endpoint returns normalized/searchable attribution only.
      // Raw provider payloads remain server-side even when an older caller
      // supplies return_raw_response=true.
      return_raw_response: false,
    },
    options
  );
}

export function postArkhamValueThread(payload, options = {}) {
  const anchor = payload?.anchor || {};
  const searchOptions = payload?.options || {};
  return postArkhamJson(
    ARKHAM_VALUE_THREAD_ENDPOINT,
    {
      client_context: 'casebuddy_value_thread',
      return_raw_response: false,
      anchor: {
        direction: anchor.direction,
        chainId: anchor.chainId,
        chainName: anchor.chainName,
        transactionHash: anchor.transactionHash,
        endpointAddress: anchor.endpointAddress,
        counterpartyAddress: anchor.counterpartyAddress,
        fromAddress: anchor.fromAddress,
        toAddress: anchor.toAddress,
        utcDate: anchor.utcDate,
        amount: anchor.economicAmount || anchor.amount,
        token: anchor.economicToken || anchor.token,
        fundsFollowed: anchor.fundsFollowed,
        priorBalance: anchor.priorBalance,
        fromHash: anchor.fromHash,
        continuesAs: anchor.continuesAs,
        leftBoundsDisplay: anchor.leftBoundsDisplay,
        leftBoundsNumeric: anchor.leftBoundsNumeric,
        hopLogic: anchor.hopLogic,
      },
      options: {
        analyzeSwaps: searchOptions.analyzeSwaps !== false,
        analyzeBridges: searchOptions.analyzeBridges !== false,
        analyzeWrapUnwrap: searchOptions.analyzeWrapUnwrap !== false,
        bridgeWindowHours: searchOptions.bridgeWindowHours,
        preferCache: searchOptions.preferCache !== false,
        forceRefresh: searchOptions.forceRefresh === true,
        maxCandidates: searchOptions.maxCandidates,
      },
    },
    options
  );
}

export function arkhamErrorMessage(error, fallback) {
  if (error instanceof ArkhamApiError && error.message) {
    return error.message;
  }
  return error?.message || fallback;
}

export const ARKHAM_ENDPOINTS = Object.freeze({
  attributionBatch: ARKHAM_ATTRIBUTION_ENDPOINT,
  noteEnrichmentBatch: ARKHAM_NOTE_ENRICHMENT_ENDPOINT,
  valueThread: ARKHAM_VALUE_THREAD_ENDPOINT,
});
